import * as _ from "lodash-es";
import axios from "axios";

import { processAxiosNetworkError } from "@/notify";
import { MAX_PAGE_SIZE } from "@/constants";

export default {
  namespaced: true,
  state: {
    selectorProfiles: {},
    events: {},
    eventSessions: [],
    children: {},
    bookings: {},
    bookingsBySession: {},
    enquiryCMSSettings: null,
    eventBookingCMSSettings: null,
    parent: null,
    initialChildData: null,
    initialBookingData: null,
    initialEnquiryData: null,
  },
  getters: {
    selectorProfilesList: (state) => Object.values(state.selectorProfiles),
    childrenList: (state) => Object.values(state.children),
    listedEventSessions: (state) => state.eventSessions.filter((s) => !s.is_unlisted),
    availableForBookingEventSessions: (state) =>
      state.eventSessions.filter((session) => !session.is_fully_booked),
    availableForBookingListedEventSessions: (state, getters) =>
      getters.listedEventSessions.filter((session) => !session.is_fully_booked),
    eventSessionsMap: (state) => _.keyBy(state.eventSessions, "id"),
  },
  mutations: {
    setSelectorProfiles(state, profiles) {
      state.selectorProfiles = _.keyBy(profiles, "id");
    },
    setParent(state, parent) {
      state.parent = parent;
    },
    setParentData(state, { parent, student_profiles }) {
      state.selectorProfiles = _.keyBy(student_profiles, "id");
      state.parent = parent;
    },
    setChildren(state, children) {
      state.children = _.keyBy(children, "id");
    },
    setBookings(state, bookings) {
      state.bookings = _.keyBy(bookings, "id");
      // Leave only last booking if parent has multiple bookings
      // with the same session.
      state.bookingsBySession = _.keyBy(_.sortBy(bookings, "created_at"), "session");
    },
    setInitialChildData(state, data) {
      state.initialChildData = data;
    },
    setInitialBookingData(state, data) {
      state.initialBookingData = data;
    },
    setInitialEnquiryData(state, data) {
      state.initialEnquiryData = data;
    },
    updateSelectorProfile(state, profile) {
      state.selectorProfiles[profile.id] = {
        ...state.selectorProfiles[profile.id],
        ...profile,
      };
    },
    setEvents(state, events) {
      state.events = _.keyBy(events, "slug");
    },
    setEventSessions(state, sessions) {
      state.eventSessions = sessions;
    },
  },
  actions: {
    /**
     * Fetch events for parent.
     * Events should always be fresh,
     * because they can be hidden, all sessions booked, etc.
     *
     * @param {Object} store The store
     * @param {Object} params Query params (filters)
     * @returns {Promise}
     */
    async fetchEvents(store, params) {
      return axios
        .get("/events/", { params: { page_size: MAX_PAGE_SIZE, ...params } })
        .then(
          (response) => {
            store.commit("setEvents", response.data.results);
            return response.data.results;
          },
          (error) => processAxiosNetworkError(error, []),
        );
    },
    /**
     * Fetch event sessions for parent.
     * Sessions should always be fresh,
     * because they can be fully booked, passed, etc.
     *
     * @param {Object} store The store
     * @param {Object} params Query params (filters)
     * @returns {Promise}
     */
    async fetchEventSessions(store, params) {
      return axios
        .get("/event-sessions/", { params: { page_size: MAX_PAGE_SIZE, ...params } })
        .then(
          (response) => {
            store.commit("setEventSessions", response.data.results);
            return response.data.results;
          },
          (error) => processAxiosNetworkError(error, []),
        );
    },
    async fetchEventBookingCMSSettings(store) {
      if (store.state.eventBookingCMSSettings) {
        return;
      }
      await axios.get("/event-booking-cms-settings/").then(
        (response) => {
          store.state.eventBookingCMSSettings = response.data;
        },
        (error) => processAxiosNetworkError(error, null),
      );
    },
    async fetchEnquiryCMSSettings(store) {
      if (store.state.enquiryCMSSettings) {
        return;
      }
      await axios.get("/enquiry-cms-settings/").then(
        (response) => {
          store.state.enquiryCMSSettings = response.data;
        },
        (error) => processAxiosNetworkError(error, null),
      );
    },
    async getParent(store) {
      if (!store.state.parent) {
        await store.dispatch("getInitialParentData");
      }
      return store.state.parent;
    },
    async getInitialParentData({ commit }) {
      const parent = await axios.get("/parents/initial/").then(
        (r) => r.data,
        (error) => processAxiosNetworkError(error, null),
      );
      commit("setParent", parent);
      return parent;
    },
    async fetchChildren({ state, commit }) {
      if (state.parent && state.parent.id) {
        const data = await axios.get("/children/").then((r) => r.data);
        commit("setChildren", data.results);
      }
      return state.children;
    },
    async fetchInitialChildData({ state, commit }) {
      if (state.initialChildData) {
        return state.initialChildData;
      }
      const initialChildData = await axios.get("/children/initial/").then((r) => r.data);
      commit("setInitialChildData", initialChildData);
      return initialChildData;
    },
    async fetchBookings({ commit }, sessionId) {
      const bookings = await axios
        .get("/event-bookings/", { params: { page_size: MAX_PAGE_SIZE } })
        .then((r) => r.data.results);
      commit("setBookings", bookings);
      const bySession = _.keyBy(bookings, "session");
      if (!bySession[sessionId]) {
        const initialData = await axios
          .get("/event-bookings/initial/")
          .then((r) => r.data);
        commit("setInitialBookingData", initialData);
      }
      return bookings;
    },
    async fetchInitialEnquiryData({ commit }) {
      const initialData = await axios.get("/enquiries/initial/").then((r) => r.data);
      commit("setInitialEnquiryData", initialData);
    },
  },
};
